import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title } from 'react-admin';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import {
    createIdempotencyKey,
    getSpeechCalibrationAdjudication,
    getSpeechCalibrationAdjudications,
    submitSpeechCalibrationAdjudication,
    type SpeechCalibrationAdjudication,
    type SpeechCalibrationAdjudicationList,
    type SpeechCalibrationPhoneVerdict,
    type SpeechCalibrationTrait,
} from '../admin/adminSpeechCalibrationApi';
import { MediaPreview } from '../examTasks/MediaPreview';
import {
    addUtcDays,
    calibrationTraitLabels,
    calibrationTraits,
    todayUtc,
} from './speechCalibrationConstants';
import { StatusChip, TraitScoreSlider } from './speechCalibrationUi';

const phoneVerdicts: Array<{ value: SpeechCalibrationPhoneVerdict; label: string }> = [
    { value: 'correct', label: 'Correction is correct' },
    { value: 'wrong', label: 'Correction is wrong' },
    { value: 'valid_accent_variant', label: 'Valid accent variant' },
    { value: 'not_sure', label: 'Not sure' },
];

function traitMap(value: number) {
    return Object.fromEntries(calibrationTraits.map((trait) => [trait, value])) as Record<SpeechCalibrationTrait, number>;
}

function compactRaterValues(
    values: Array<{ reviewerAlias: string; traits: Array<{ trait: SpeechCalibrationTrait; value: number }> }>,
) {
    return values.map((rating) => (
        <Stack key={rating.reviewerAlias} direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
            <Chip size="small" label={rating.reviewerAlias} sx={{ fontWeight: 800 }} />
            {rating.traits.map((trait) => (
                <Chip
                    key={`${rating.reviewerAlias}-${trait.trait}`}
                    size="small"
                    variant="outlined"
                    label={`${calibrationTraitLabels[trait.trait]} ${trait.value.toFixed(1)}`}
                />
            ))}
        </Stack>
    ));
}

export function SpeechCalibrationAdjudicationPage() {
    const navigate = useNavigate();
    const currentDay = todayUtc();
    const [selectedDay, setSelectedDay] = useState(currentDay);
    const [status, setStatus] = useState('pending');
    const [data, setData] = useState<SpeechCalibrationAdjudicationList | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            setData(await getSpeechCalibrationAdjudications({
                dateUtc: selectedDay,
                status,
                page: 1,
                pageSize: 100,
            }));
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load adjudications');
        } finally {
            setIsLoading(false);
        }
    }, [selectedDay, status]);

    useEffect(() => {
        void load();
    }, [load]);

    return (
        <Box sx={{ p: { xs: 1.25, md: 2.5 }, minWidth: 0 }}>
            <Title title="Speech Calibration Adjudication" />
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ color: '#0f172a', fontWeight: 900 }}>Adjudication queue</Typography>
                    <Typography sx={{ mt: 0.4, color: '#64748b' }}>
                        Resolve material rater disagreements after both independent judgments are complete.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                    disabled={isLoading}
                    onClick={() => void load()}
                    sx={{ alignSelf: { xs: 'stretch', md: 'flex-start' } }}
                >
                    Refresh
                </Button>
            </Stack>

            <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2.5 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ArrowBackIosNewIcon />}
                            onClick={() => setSelectedDay(addUtcDays(selectedDay, -1))}
                        >
                            Previous
                        </Button>
                        <Button
                            size="small"
                            variant={selectedDay === currentDay ? 'contained' : 'outlined'}
                            onClick={() => setSelectedDay(currentDay)}
                        >
                            Today
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<ArrowForwardIosIcon />}
                            disabled={selectedDay >= currentDay}
                            onClick={() => setSelectedDay(addUtcDays(selectedDay, 1))}
                        >
                            Next
                        </Button>
                        <Typography sx={{ color: '#64748b', fontSize: '0.82rem', fontWeight: 800 }}>{selectedDay} UTC</Typography>
                    </Stack>
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel id="adjudication-status-label">Status</InputLabel>
                        <Select
                            labelId="adjudication-status-label"
                            label="Status"
                            value={status}
                            onChange={(event) => setStatus(String(event.target.value))}
                        >
                            <MenuItem value="">All statuses</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Paper>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontWeight: 850 }}>{data?.total ?? 0} adjudications</Typography>
                </Box>
                {isLoading && !data ? (
                    <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 850 }}>Task</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Question</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Rater judgments</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Model</TableCell>
                                    <TableCell sx={{ fontWeight: 850 }}>Status</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 850 }}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.items.map((item) => (
                                    <TableRow key={item.adjudicationId} hover>
                                        <TableCell>
                                            <Typography sx={{ fontWeight: 750 }}>{item.examTaskType}</Typography>
                                            <Typography sx={{ color: '#64748b', fontSize: '0.75rem' }}>{item.examName}</Typography>
                                        </TableCell>
                                        <TableCell>{item.questionNo ?? '—'}</TableCell>
                                        <TableCell><Stack spacing={0.5}>{compactRaterValues(item.raterValues)}</Stack></TableCell>
                                        <TableCell>{item.modelRevision}</TableCell>
                                        <TableCell><StatusChip status={item.status} /></TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => navigate(`/speech-calibration/adjudication/${item.adjudicationId}`)}
                                            >
                                                {item.status === 'completed' ? 'View' : 'Resolve'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                {!isLoading && (data?.items.length ?? 0) === 0 ? (
                    <Typography sx={{ py: 5, textAlign: 'center', color: '#64748b' }}>
                        No adjudications match this day and status.
                    </Typography>
                ) : null}
            </Paper>
        </Box>
    );
}

export function SpeechCalibrationAdjudicationDetailPage() {
    const { adjudicationId = '' } = useParams();
    const navigate = useNavigate();
    const [detail, setDetail] = useState<SpeechCalibrationAdjudication | null>(null);
    const [scores, setScores] = useState<Record<SpeechCalibrationTrait, number>>(traitMap(2.5));
    const [touched, setTouched] = useState<Set<SpeechCalibrationTrait>>(new Set());
    const [phoneDecisions, setPhoneDecisions] = useState<Record<string, SpeechCalibrationPhoneVerdict>>({});
    const [notes, setNotes] = useState('');
    const [idempotencyKey] = useState(() => createIdempotencyKey('adjudication'));
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [complete, setComplete] = useState(false);

    useEffect(() => {
        if (!adjudicationId) {
            setError('Adjudication ID is missing');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        getSpeechCalibrationAdjudication(adjudicationId)
            .then((nextDetail) => {
                setDetail(nextDetail);
                setComplete(nextDetail.status === 'completed');
            })
            .catch((loadError: unknown) => setError(loadError instanceof Error ? loadError.message : 'Unable to load adjudication'))
            .finally(() => setIsLoading(false));
    }, [adjudicationId]);

    const ready = useMemo(
        () => calibrationTraits.every((trait) => touched.has(trait)) &&
            (detail?.phoneCandidates ?? []).every((candidate) => Boolean(phoneDecisions[candidate.candidateId])),
        [detail?.phoneCandidates, phoneDecisions, touched],
    );

    const submit = async () => {
        if (!detail || !ready) {
            setError('Set every final trait and phoneme judgment.');
            return;
        }
        setIsSubmitting(true);
        setError(null);
        try {
            await submitSpeechCalibrationAdjudication(detail.adjudicationId, {
                traits: calibrationTraits.map((trait) => ({ trait, value: scores[trait] })),
                phones: detail.phoneCandidates.map((candidate) => ({
                    candidateId: candidate.candidateId,
                    verdict: phoneDecisions[candidate.candidateId],
                })),
                reasonCodes: detail.reasonCodes,
                notes: notes.trim(),
                idempotencyKey,
            });
            setComplete(true);
        } catch (submitError) {
            setError(submitError instanceof Error ? submitError.message : 'Unable to complete adjudication');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 420 }}><CircularProgress /></Box>;
    }

    return (
        <Box sx={{ p: { xs: 1.25, md: 2.5 }, minWidth: 0 }}>
            <Title title="Speech Calibration Adjudication" />
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/speech-calibration/adjudication')} sx={{ mb: 1 }}>
                Adjudication queue
            </Button>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {complete ? <Alert severity="success" sx={{ mb: 2 }}>This adjudication is complete.</Alert> : null}

            {detail ? (
                <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Typography variant="h4" sx={{ fontWeight: 900 }}>Senior judgment</Typography>
                        <Typography sx={{ mt: 0.4, color: '#64748b' }}>
                            {detail.examName} · {detail.examTaskType} · Question {detail.questionNo ?? '—'}
                        </Typography>
                        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                            {detail.reasonCodes.map((reason) => (
                                <Chip key={reason} size="small" color="warning" variant="outlined" label={reason.replaceAll('_', ' ')} />
                            ))}
                        </Stack>
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Typography sx={{ mb: 1.25, fontWeight: 850 }}>Recording and prompt</Typography>
                        <MediaPreview url={detail.audioUrl} title="Learner response" width="100%" showWaveform mediaKind="audio" />
                        {detail.referenceText ? (
                            <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, backgroundColor: '#f8fafc' }}>
                                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{detail.referenceText}</Typography>
                            </Paper>
                        ) : null}
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                        <Typography sx={{ mb: 1.5, fontWeight: 850 }}>Independent judgments</Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                                gap: 1.5,
                            }}
                        >
                            {detail.blindRatings.map((rating) => (
                                <Paper key={rating.reviewerAlias} variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography sx={{ mb: 1, fontWeight: 850 }}>{rating.reviewerAlias}</Typography>
                                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                                        {rating.traits.map((trait) => (
                                            <Chip
                                                key={trait.trait}
                                                label={`${calibrationTraitLabels[trait.trait]} ${trait.value.toFixed(1)} · ${trait.confidence}`}
                                                variant="outlined"
                                            />
                                        ))}
                                    </Stack>
                                    {rating.flags.length ? (
                                        <Typography sx={{ mt: 1, color: '#64748b', fontSize: '0.78rem' }}>
                                            {rating.flags.map((flag) => flag.replaceAll('_', ' ')).join(', ')}
                                        </Typography>
                                    ) : null}
                                </Paper>
                            ))}
                        </Box>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                            {detail.modelValues.map((trait) => (
                                <Chip
                                    key={trait.trait}
                                    color="secondary"
                                    label={`Model ${calibrationTraitLabels[trait.trait]} ${trait.value?.toFixed(1) ?? '—'}`}
                                />
                            ))}
                        </Stack>
                    </Paper>

                    {!complete ? (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
                            <Typography sx={{ mb: 1.5, fontWeight: 850 }}>Final adjudicated labels</Typography>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                                    gap: 2.5,
                                }}
                            >
                                {calibrationTraits.map((trait) => (
                                    <TraitScoreSlider
                                        key={trait}
                                        trait={trait}
                                        value={scores[trait]}
                                        onChange={(value) => {
                                            setScores((current) => ({ ...current, [trait]: value }));
                                            setTouched((current) => new Set(current).add(trait));
                                        }}
                                    />
                                ))}
                            </Box>

                            {detail.phoneCandidates.length ? (
                                <Stack spacing={1} sx={{ mt: 2.5 }}>
                                    <Typography sx={{ fontWeight: 850 }}>Final phoneme judgments</Typography>
                                    {detail.phoneCandidates.map((candidate) => (
                                        <Paper key={candidate.candidateId} variant="outlined" sx={{ p: 1.5 }}>
                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                                                <Box sx={{ minWidth: 260 }}>
                                                    <Typography sx={{ fontWeight: 800 }}>{candidate.word}</Typography>
                                                    <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                                                        /{candidate.targetPhone}/ → /{candidate.observedPhone}/ · {candidate.startSeconds.toFixed(2)}–{candidate.endSeconds.toFixed(2)}s
                                                    </Typography>
                                                </Box>
                                                <FormControl size="small" fullWidth>
                                                    <InputLabel id={`${candidate.candidateId}-adjudication-phone-label`}>Final verdict</InputLabel>
                                                    <Select
                                                        labelId={`${candidate.candidateId}-adjudication-phone-label`}
                                                        label="Final verdict"
                                                        value={phoneDecisions[candidate.candidateId] ?? ''}
                                                        onChange={(event) => setPhoneDecisions((current) => ({
                                                            ...current,
                                                            [candidate.candidateId]: event.target.value as SpeechCalibrationPhoneVerdict,
                                                        }))}
                                                    >
                                                        {phoneVerdicts.map((verdict) => (
                                                            <MenuItem key={verdict.value} value={verdict.value}>{verdict.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Stack>
                                        </Paper>
                                    ))}
                                </Stack>
                            ) : null}

                            <TextField
                                label="Adjudication note"
                                value={notes}
                                onChange={(event) => setNotes(event.target.value)}
                                multiline
                                minRows={2}
                                fullWidth
                                sx={{ mt: 2 }}
                                helperText="Do not enter learner transcripts or personal information."
                            />
                            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled={!ready || isSubmitting}
                                    onClick={() => void submit()}
                                    startIcon={isSubmitting ? <CircularProgress size={17} color="inherit" /> : undefined}
                                >
                                    Complete adjudication
                                </Button>
                            </Stack>
                        </Paper>
                    ) : null}
                </Stack>
            ) : (
                <Alert severity="warning">This adjudication is unavailable.</Alert>
            )}
        </Box>
    );
}
